import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";
import { base } from "../../constants/apis";

type FXApiResponse = {
  status: "success" | "fail";
  data: {
    quotes: {
      [date: string]: {
        USDINR: number;
      };
    };
  };
};

type CurrencyDataPoint = {
  timestamp: string;
  value: number;
};

type Arg = { type: "Year" | "Month" | "Day"; body: any };

const getSimplifiedData = (
  data: CurrencyDataPoint[],
  range: "Year" | "Month" | "Day"
) => {
  if (range === "Year") {
    const interval = Math.ceil(data.length / 30); // Keep around 30 points for year view
    return data.filter((_, i) => i % interval === 0);
  }

  if (range === "Month") {
    const interval = Math.ceil(data.length / 60); // Keep around 60 points for month view
    return data.filter((_, i) => i % interval === 0);
  }

  // For "Day" range, keep all points
  return data.reverse();
};

const getFilteredData = (
  response: FXApiResponse,
  arg: Arg
): CurrencyDataPoint[] => {
  if (!response || response.status !== "success" || !response.data?.quotes) {
    return [];
  }

  const rawData = Object.entries(response.data.quotes).map(
    ([date, { USDINR }]) => ({
      timestamp: date,
      value: USDINR,
    })
  );

  return getSimplifiedData(rawData, arg.type);
};

export const fxApi = createApi({
  reducerPath: "fxApi",
  baseQuery: fetchBaseQuery({
    baseUrl: base,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getFxRate: builder.mutation<CurrencyDataPoint[], Arg>({
      query: ({ type, body }) => ({
        url: `/apis/common/date_range_currency_exchange.php?type=${type}`,
        method: "POST",
        body,
      }),
      transformResponse: (response: FXApiResponse, _, arg) =>
        getFilteredData(response, arg),
    }),
  }),
});

export const { useGetFxRateMutation } = fxApi;
